import type { ApiClient } from "./client.js";
import type { PublicEmployee, WireEmployeeWithCode } from "./types.js";

/**
 * Employee (PIN) management, driven by the desktop setup wizard and settings
 * screen. The list is the same public shape everything else uses — the `code`
 * is never returned (see `publicEmployee` in contracts) — so existing PINs are
 * never shown; the operator can add a new one or deactivate an old one, not
 * read the current secret back.
 *
 * The write calls are held to a stricter bar server-side than the rest of the
 * API (`apps/server/src/routes/employees.ts`): on desktop only the till itself
 * (loopback) may call them, on hosted only an admin.
 */
export function listEmployees(client: ApiClient): Promise<{ employees: PublicEmployee[] }> {
  return client.get<{ employees: PublicEmployee[] }>("/api/employees");
}

/**
 * The admin/till view that DOES include each code — backs the "Kode zaposlenih"
 * management screen. Server-gated to an admin (hosted) or the loopback till
 * (desktop); a staff login gets a 403 here.
 */
export function listManagedEmployees(client: ApiClient): Promise<{ employees: WireEmployeeWithCode[] }> {
  return client.get<{ employees: WireEmployeeWithCode[] }>("/api/employees/managed");
}

export interface NewEmployeeBody {
  name: string;
  lastname: string;
  code: string;
}

export function createEmployee(client: ApiClient, body: NewEmployeeBody): Promise<{ employee: PublicEmployee }> {
  return client.post<{ employee: PublicEmployee }>("/api/employees", body);
}

export interface EmployeePatchBody {
  name?: string;
  lastname?: string;
  active?: boolean;
  /** Re-point the PIN. A collision comes back as a 409 `conflict` with reason `duplicate-code`. */
  code?: string;
}

export function updateEmployee(client: ApiClient, id: string, patch: EmployeePatchBody): Promise<{ employee: PublicEmployee }> {
  return client.patch<{ employee: PublicEmployee }>(`/api/employees/${id}`, patch);
}
